import React from 'react';

export const formatReviewText_v2 = (text: string, isMobile?: boolean, scoreColor?: string): React.ReactNode => {
  if (!text) return text;

  // テキストを一旦単一の文字列として処理
  let formattedText = text;

  // スポイラー処理（複数行対応）
  formattedText = formattedText.replace(
    /\[spoiler\]([\s\S]*?)(?:\[\/spoiler\]|$)/g,
    (match: string, content: string) => {
      const processedContent = content.replace(/\n/g, '<br>');
      return `<span class="spoiler" style="background-color: #303030; color: #303030; cursor: pointer; padding: 0 4px; border-radius: 2px; user-select: none;" onclick="this.style.backgroundColor='transparent'; this.style.color='inherit';">${processedContent}</span>`;
    }
  );

  // 引用（複数行対応）- より洗練されたスタイル
  formattedText = formattedText.replace(
    /\[quote\]([\s\S]*?)\[\/quote\]/g,
    (match: string, content: string) => {
      const processedContent = content.replace(/\n/g, '<br>');
      return `<blockquote style="background-color: rgba(255, 255, 255, 0.03); border-left: 4px solid ${scoreColor || '#666'}; margin: 1.5em 0; padding: 1em 1.5em; color: rgba(255, 255, 255, 0.7); border-radius: 4px; font-style: italic;">${processedContent}</blockquote>`;
    }
  );

  // リスト処理（複数行対応）
  formattedText = formattedText.replace(
    /\[list\]([\s\S]*?)\[\/list\]/g,
    (match: string, content: string) => {
      const items = content
        .split('[*]')
        .filter((item: string) => item.trim())
        .map((item: string) => `<li style="margin: 0.5em 0;">${item.trim()}</li>`)
        .join('');
      return `<ul style="margin: 1em 0; padding-left: 1.8em; line-height: 1.8;">${items}</ul>`;
    }
  );

  formattedText = formattedText.replace(
    /\[olist\]([\s\S]*?)\[\/olist\]/g,
    (match: string, content: string) => {
      const items = content
        .split('[*]')
        .filter((item: string) => item.trim())
        .map((item: string) => `<li style="margin: 0.5em 0;">${item.trim()}</li>`)
        .join('');
      return `<ol style="margin: 1em 0; padding-left: 1.8em; line-height: 1.8;">${items}</ol>`;
    }
  );

  // 処理済みのテキストを行に分割
  const lines = formattedText.split('\n');

  const formatLine = (line: string): React.ReactElement => {
    let processedText = line;

    // ヘッダー - ゲームメディア風の洗練されたスタイル
    const h1Size = isMobile ? '1.3rem' : '1.75rem';
    const h2Size = isMobile ? '1.15rem' : '1.4rem';
    const h3Size = isMobile ? '1rem' : '1.15rem';

    // H1: 大きな見出し、下線付き
    processedText = processedText.replace(
      /\[h1\](.*?)\[\/h1\]/g,
      `<h2 style="
        font-size: ${h1Size};
        margin: 2em 0 1em 0;
        padding-bottom: 0.5em;
        color: #fff;
        font-weight: 700;
        border-bottom: 3px solid ${scoreColor || 'rgba(255, 255, 255, 0.2)'};
        letter-spacing: 0.5px;
      ">$1</h2>`
    );

    // H2: 中見出し、左ボーダー付き
    processedText = processedText.replace(
      /\[h2\](.*?)\[\/h2\]/g,
      `<h3 style="
        font-size: ${h2Size};
        margin: 1.5em 0 0.75em 0;
        padding-left: 0.75em;
        color: #f8f8f8;
        font-weight: 600;
        border-left: 4px solid ${scoreColor || 'rgba(255, 255, 255, 0.3)'};
        letter-spacing: 0.3px;
      ">$1</h3>`
    );

    // H3: 小見出し、シンプルな下線
    processedText = processedText.replace(
      /\[h3\](.*?)\[\/h3\]/g,
      `<h4 style="
        font-size: ${h3Size};
        margin: 1.25em 0 0.5em 0;
        padding-bottom: 0.25em;
        color: #f0f0f0;
        font-weight: 600;
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
      ">$1</h4>`
    );

    // インライン装飾
    processedText = processedText.replace(
      /\[b\](.*?)\[\/b\]/g,
      '<strong style="font-weight: 700; color: rgba(255, 255, 255, 0.95);">$1</strong>'
    );
    processedText = processedText.replace(
      /\[i\](.*?)\[\/i\]/g,
      '<em style="font-style: italic; color: rgba(255, 255, 255, 0.85);">$1</em>'
    );
    processedText = processedText.replace(
      /\[s\](.*?)\[\/s\]/g,
      '<del style="opacity: 0.7;">$1</del>'
    );
    processedText = processedText.replace(
      /\[u\](.*?)\[\/u\]/g,
      `<u style="text-decoration-color: ${scoreColor || 'rgba(255, 255, 255, 0.4)'};">$1</u>`
    );

    // URLリンク - より目立つスタイル
    processedText = processedText.replace(
      /\[url=(.*?)\](.*?)\[\/url\]/g,
      `<a href="$1" target="_blank" rel="noopener noreferrer" style="color: ${scoreColor || '#66c0f4'}; text-decoration: none; border-bottom: 1px solid ${scoreColor || '#66c0f4'}; transition: opacity 0.2s; font-weight: 500;">$2</a>`
    );

    return React.createElement('p', {
      style: { margin: '0.5em 0', lineHeight: 'inherit' },
      dangerouslySetInnerHTML: { __html: processedText }
    });
  };

  // 各行を個別に処理して配列として返す
  const formattedLines = lines.map((line, index) =>
    line.trim() ? formatLine(line) : React.createElement('br', { key: `br-${index}` })
  );

  return React.createElement('div', { style: { lineHeight: 'inherit' } }, formattedLines);
};
